import TableComponent from 'components/Table';
import { useInvoicesHoldingCashQuery } from 'hooks/useInvoiceHoldingCashQuery';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusTag } from 'screens/deliveries/package-tracker/components/table/StatusTag';
import {
  formatNumberWithComma,
  getStakeholderTypeForCountry,
  renderDate,
  renderInfoValue,
} from 'utils/helpers';
import { paymentStatusMap } from '../helper';
import { Invoice, InvoiceList } from '../types';

interface InvoiceTableProps {
  country: string;
  data: InvoiceList;
  loading: boolean;
  pagination?: {
    total: number;
    current: number;
    pageSize: number;
    onChange: (page: number, size: number) => void;
    onShowSizeChange: (page: number, size: number) => void;
  };
}

const InvoiceTable = ({
  country,
  data,
  loading,
  pagination,
}: InvoiceTableProps) => {
  const { t } = useTranslation();

  const nonPaidInvoiceIds = useMemo(() => {
    return data
      .filter((invoice) => invoice?.status !== 'paid')
      .map((invoice) => invoice.invoiceId);
  }, [data]);

  const { data: holdingCashInvoicesMap } =
    useInvoicesHoldingCashQuery(nonPaidInvoiceIds);

  const renderTableHeader = useCallback(() => {
    return [
      {
        title: t('collectionInvoices.table.invoice'),
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        render: (text: string) => renderInfoValue(text, 'N/A'),
        width: 150,
      },
      {
        title: t('collectionInvoices.table.amount'),
        dataIndex: 'amount',
        key: 'amount',
        render: (text: string, record: Invoice) =>
          `${record?.currencyCode} ${formatNumberWithComma(text) || 0}`,
      },
      {
        title: t('collectionInvoices.table.paymentStatus'),
        dataIndex: 'status',
        key: 'status',
        render: (text: Invoice['status'], record: Invoice) => {
          const isHoldingCash =
            holdingCashInvoicesMap?.[record?.invoiceId]?.isHoldingCash;
          return (
            <StatusTag
              status={isHoldingCash ? 'holdingCash' : text}
              statusConfigMap={paymentStatusMap}
            />
          );
        },
        width: 200,
      },
      {
        title: t('collectionInvoices.table.farmerGroup'),
        dataIndex: ['farmerGroup', 'name'],
        key: 'farmerGroupName',
        render: (text: string) => renderInfoValue(text, 'N/A'),
      },
      {
        title: t('collectionInvoices.table.agronomist'),
        dataIndex: ['salesPerson', 'name'],
        key: 'agronomistName',
        render: (text: string) => renderInfoValue(text, 'N/A'),
      },
      {
        title: t('collectionInvoices.table.district'),
        dataIndex: ['farmerGroup', 'district'],
        key: 'district',
        render: (text: string) => renderInfoValue(text, 'N/A'),
      },
      {
        title: t('collectionInvoices.table.subDistrict'),
        dataIndex: ['farmerGroup', 'subDistrict'],
        key: 'subDistrict',
        render: (text: string) => renderInfoValue(text, 'N/A'),
      },
      {
        title: t('collectionInvoices.table.dueDate'),
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (text: string) => renderDate(text, 'N/A'),
        width: 200,
      },
      {
        title: t('collectionInvoices.table.financeDueDate'),
        dataIndex: 'financeDueDate',
        key: 'financeDueDate',
        render: (text: string) => renderDate(text, 'N/A'),
        width: 200,
      },
      {
        title: t('collectionInvoices.table.ehd'),
        dataIndex: ['seasonParticipants', 'estimatedHarvestDate'],
        key: 'ehd',
        render: (text: string) => renderDate(text, 'N/A'),
        width: 200,
      },
      {
        title: t('collectionInvoices.table.updateTimestamp'),
        dataIndex: 'ehdUpdatedDate',
        key: 'updateTimeStamp',
        width: 200,
        render: (text: string) =>
          renderDate(text, 'N/A', 'DD/MM/YYYY HH:mm:ss A'),
      },
      {
        title: getStakeholderTypeForCountry(country, 'PB'),
        dataIndex: ['paddyBuyer', 'name'],
        key: 'paddyBuyerName',
        render: (text: string) => renderInfoValue(text, 'N/A'),
      },
      {
        title: t('collectionInvoices.table.paddyBuyerPhone'),
        dataIndex: ['paddyBuyer', 'phoneNumber'],
        key: 'paddyBuyerPhoneNumber',
        render: (text: string) => renderInfoValue(text, 'N/A'),
      },
    ];
  }, [t, holdingCashInvoicesMap]);

  return (
    <TableComponent
      loading={loading}
      pagination={pagination}
      tableColumns={data}
      tableHeader={renderTableHeader()}
      // to minimize cumulative layout shift
      style={{ minHeight: loading ? '40vh' : 0 }}
    />
  );
};

export default InvoiceTable;
